import { fetchWithRetry } from '../utils/fetchWithRetry';
import { apiKeyService, AIProvider } from './apiKeyService';

function getAuthHeaders(provider?: AIProvider): Record<string, string> {
  const p = provider || apiKeyService.getActiveProvider();
  const headers: Record<string, string> = {
    'X-AI-Provider': p,
  };

  const apiKey = apiKeyService.getKey(p);
  if (!apiKey) {
    console.warn(`[aiService] No API key for ${p}. Set one in Settings > AI Settings.`);
  }
  headers['X-API-Key'] = apiKey;
  return headers;
}

function getErrorMessage(provider: AIProvider): string {
  const names: Record<AIProvider, string> = {
    openai: 'OpenAI',
    gemini: 'Google Gemini',
    anthropic: 'Anthropic Claude',
  };
  return `No API key set for ${names[provider]}. Please enter your key in Settings > AI Settings.`;
}

export async function callAI(
  messages: { role: string; content: string }[],
  options: {
    temperature?: number;
    maxTokens?: number;
    jsonMode?: boolean;
    provider?: AIProvider;
  } = {}
): Promise<string> {
  const provider = options.provider || apiKeyService.getActiveProvider();

  if (apiKeyService.getAuthMode() === 'byok') {
    if (!apiKeyService.hasKey(provider)) {
      throw new Error(getErrorMessage(provider));
    }
  }

  const { temperature = 0.7, maxTokens = 2000, jsonMode = false } = options;
  const model = apiKeyService.getSelectedModel(provider);

  const response = await fetchWithRetry('/api/generate', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(provider),
    },
    body: JSON.stringify({ messages, temperature, maxTokens, jsonMode, provider, model }),
    timeout: 90000,
    retries: 2,
  });

  const data = await response.json();

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Invalid API key. Please set a valid key in Settings.');
    }
    const message = data?.error || 'Failed to generate tweet. Please try again later.';
    throw new Error(message);
  }

  if (!data?.content) {
    throw new Error('Failed to retrieve generated content.');
  }

  return data.content;
}

export async function transcribeAudio(audioBlob: Blob): Promise<string> {
  // Transcription currently only supported by OpenAI
  const provider: AIProvider = 'openai';

  if (apiKeyService.getAuthMode() === 'byok' && !apiKeyService.hasKey(provider)) {
    throw new Error('Voice transcription requires an OpenAI API key. Please enter your key in Settings.');
  }

  const formData = new FormData();
  formData.append('audio', audioBlob, 'audio.webm');

  const response = await fetchWithRetry('/api/transcribe', {
    method: 'POST',
    headers: {
      ...getAuthHeaders(provider),
    },
    body: formData,
    timeout: 60000,
    retries: 1,
  });

  const data = await response.json();

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Invalid API key. Please set a valid key in Settings.');
    }
    const message = data?.error || 'Failed to transcribe audio.';
    throw new Error(message);
  }

  if (!data?.text) {
    throw new Error('Failed to retrieve transcription result.');
  }

  return data.text;
}
